/** Inline category editor для ячейки в products list table.
 *
 *  Default state: текст-кнопка с именем категории. На hover — pencil icon.
 *  Клик → раскрывается searchable picker. Выбор → PATCH /products/{id}
 *  с category_id (+ синхронизация product_categories через PUT /categories,
 *  чтобы primary в junction-таблице тоже обновилась).
 *
 *  Optimistic UI: query сразу инвалидируется + toast.success.
 *  Ошибка → toast.error + откат на старое значение (rollback через
 *  обычный refetch — react-query сам подтянет правильные данные).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { PrimaryCategoryPicker } from "@/components/products/CategoryPicker";
import { api } from "@/lib/api";

interface Props {
  productId: number;
  categoryId: number;
  categoryName: string;
}

export function InlineCategoryCell({
  productId,
  categoryId,
  categoryName,
}: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const mutation = useMutation({
    mutationFn: async (newCategoryId: number) => {
      // 1. Меняем primary category на product'е
      await api.patch(`/products/${productId}`, { category_id: newCategoryId });
      // 2. Синхронизируем junction-таблицу (secondary не трогаем —
      //    бэкенд сам обновит is_primary при PATCH через update_product).
      //    Тут нет PUT /categories чтобы не стирать secondary.
    },
    onSuccess: () => {
      toast.success("Категория изменена");
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      queryClient.invalidateQueries({
        queryKey: ["admin-product-categories", productId],
      });
      setEditing(false);
    },
    onError: (e: any) => {
      const msg =
        e?.response?.data?.error?.message ?? "Не удалось изменить категорию";
      toast.error(msg);
    },
  });

  if (editing) {
    return (
      <div className="min-w-[260px] max-w-[320px]">
        <PrimaryCategoryPicker
          value={categoryId}
          onChange={(newId) => {
            if (newId !== categoryId) {
              mutation.mutate(newId);
            } else {
              setEditing(false);
            }
          }}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="group inline-flex items-center gap-1.5 rounded text-left text-xs text-muted-foreground hover:text-primary"
      title="Кликни чтобы изменить категорию"
    >
      <span className="truncate">{categoryName}</span>
      <Pencil className="h-3 w-3 opacity-0 transition group-hover:opacity-100" />
    </button>
  );
}
